import { useAuth } from '@/contexts/auth-context';
import { invalidateCollabRealtimeQueries } from '@/hooks/queries/collab-keys';
import { invalidateDashboardOverview } from '@/hooks/queries/dashboard-overview-keys';
import { notificationQueryKeys } from '@/hooks/queries/use-notifications-query';
import { applicationKeys } from '@/hooks/queries/use-applications-query';
import {
  getNotificationsTransmitClient,
  resetNotificationsTransmitClient,
} from '@/libs/notifications-transmit';
import {
  dashboardNotificationsChannel,
  NotificationCategory,
  NotificationRealtimeEventName,
  NotificationType,
  type NotificationWakeupEvent,
} from '@fluenceur/types';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

const SUBSCRIBE_TIMEOUT_MS = 10_000;

export function NotificationsTransmitBridge() {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      resetNotificationsTransmitClient();
      return;
    }

    let cancelled = false;
    const transmit = getNotificationsTransmitClient();
    const subscription = transmit.subscription(dashboardNotificationsChannel(userId));

    subscription.onMessage((event: NotificationWakeupEvent) => {
      if (event.event !== NotificationRealtimeEventName.CREATED) return;

      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all });

      if (
        event.type === NotificationType.BRAND_SPACE_APPLICATION_RECEIVED ||
        event.type === NotificationType.BRIEF_APPLICATION_RECEIVED
      ) {
        queryClient.invalidateQueries({ queryKey: applicationKeys.all });
        queryClient.invalidateQueries({ queryKey: ['program-invitations'] });
        invalidateDashboardOverview(queryClient);
      }

      if (event.category === NotificationCategory.AGENT) {
        queryClient.invalidateQueries({ queryKey: ['recommendations'] });
        queryClient.invalidateQueries({ queryKey: ['recommendations-history'] });
        queryClient.invalidateQueries({ queryKey: ['recommendations-history-batches'] });
      }

      // A collab event (viewed / accepted / declined) landed: refresh every
      // collab query and the contact summaries (same scope as the mutation
      // invalidator, one level up since no brand id rides the event).
      if (event.category === NotificationCategory.COLLAB) {
        invalidateCollabRealtimeQueries(queryClient);
      }
    });

    const subscribe = async () => {
      try {
        await Promise.race([
          subscription.create(),
          new Promise((_, reject) =>
            window.setTimeout(
              () => reject(new Error('Notification subscription timed out')),
              SUBSCRIBE_TIMEOUT_MS
            )
          ),
        ]);

        if (!cancelled && !subscription.isCreated) {
          console.warn('[NOTIFICATIONS] Notification subscription was not authorized');
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('[NOTIFICATIONS] Failed to subscribe to notification events', error);
        }
      }
    };

    subscribe();

    return () => {
      cancelled = true;
      subscription.delete().catch((error) => {
        console.warn('[NOTIFICATIONS] Failed to unsubscribe from notification events', error);
      });
    };
  }, [isAuthenticated, queryClient, userId]);

  return null;
}
